import { Checkbox as AriaCheckbox, type CheckboxProps } from '@ariakit/react';
import { useFormikContext } from 'formik';
import type {
  ComponentPropsWithRef,
  FC,
  InputHTMLAttributes,
  ReactNode,
  RefObject,
} from 'react';
import { useEffect, useId } from 'react';
import mergeRefs from 'react-merge-refs';
import {
  CheckedIcon,
  MixedCheck,
  UncheckedIcon,
} from 'src/components/form/checkbox-icons';
import { ErrorMessage } from 'src/components/form/error-message';
import { useFieldGroup } from 'src/components/form/field-group';
import { FieldItem, FieldLayout } from 'src/components/form/field-layout';
import type { FormFieldProps } from 'src/components/form/form-field-props';
import { FormHint } from 'src/components/form/form-hint';
import { FieldLabel } from 'src/components/form/label';
import { useCustomField } from 'src/components/form/use-custom-field';
import { useFocusManager } from 'src/components/form/use-focus-manager';
import { Tooltip } from 'src/components/overlay/tooltip';
import { useDefaultStacked } from 'src/hooks/use-default-stacked';
import { focusWithinClasses } from 'src/styles';
import { cn } from 'src/utils/cn';
import type { StrictOmit } from 'src/utils/utility-types';

export type SimpleCheckboxProps = {
  label: ReactNode;
  labelRef?: RefObject<HTMLLabelElement>;
  showError?: boolean;
  hideLabel?: boolean;
  disabled?: boolean;
  disabledMessage?: ReactNode;
  id?: string;
  'data-testid'?: string;
} & StrictOmit<ComponentPropsWithRef<'input'>, 'checked'> &
  CheckboxProps;

export const SimpleCheckbox: FC<SimpleCheckboxProps> = ({
  label,
  showError,
  checked,
  disabled,
  disabledMessage,
  labelRef,
  hideLabel,
  ref,
  ...inputProps
}) => {
  const focusRef = useFocusManager();

  return (
    <Tooltip
      content={disabled && disabledMessage ? disabledMessage : undefined}
    >
      {(targetProps) => (
        <span className="wrap-break-word" {...targetProps}>
          <label
            className={`
              inline-grid grid-flow-col items-baseline gap-xs
              legacy:gap-[0.5rem]
            `}
            ref={labelRef}
          >
            <span
              className={cn(
                `
                  relative top-icon-offset inline-flex size-[1.5rem]
                  not-legacy:rounded-2xs
                `,
                focusWithinClasses,
                `focus-within:spaced-focus-style!`,
                disabled && 'cursor-not-allowed'
              )}
            >
              <AriaCheckbox
                data-testid={inputProps.name}
                {...inputProps}
                checked={checked}
                disabled={disabled}
                // Here we need to map again because of CustomCheckbox requirements (it expects a string)
                // other ways it throws a numerous console errors
                value={Boolean(inputProps.value).toString()}
                ref={mergeRefs([focusRef, ref!])}
                className="absolute top-0 left-0 opacity-0"
              />

              {checked === 'mixed' ? (
                <MixedCheck error={showError} disabled={disabled} />
              ) : checked === true ? (
                <CheckedIcon error={showError} disabled={disabled} />
              ) : (
                <UncheckedIcon error={showError} disabled={disabled} />
              )}
            </span>
            {!hideLabel && (
              <span
                className={cn(
                  `
                    w-fit text-foreground-default select-none
                    not-legacy:text-body-base
                    legacy:text-legacy-brand
                  `,
                  showError &&
                    `
                      text-foreground-support-error
                      legacy:text-legacy-error
                    `,
                  disabled &&
                    `
                      cursor-not-allowed text-foreground-surface-disabled
                      legacy:text-legacy-inactive
                    `
                )}
              >
                {label}
              </span>
            )}
          </label>
        </span>
      )}
    </Tooltip>
  );
};

type Props = {
  optionLabel: ReactNode;
  stacked?: boolean;
} & FormFieldProps &
  StrictOmit<InputHTMLAttributes<HTMLInputElement>, 'checked'> &
  CheckboxProps;

/**
 * Use this for yes/no decisions from the user.
 */
export const Checkbox: FC<Props> = (props) => {
  const { isStacked, minTablet } = useDefaultStacked();

  const {
    label,
    optionLabel,
    name,
    checked,
    stacked = isStacked,
    hideLabel = false,
    info,
    hint,
    disabledMessage,
    ...inputProps
  } = props;
  const id = useId();
  const { setFieldValue } = useFormikContext<boolean>();
  const [field, showError, error] = useCustomField<boolean>(name, label);
  const formGroup = useFieldGroup();
  useEffect(() => {
    if (formGroup) {
      formGroup.onError({ name, showError });
    }
  }, [showError, name, formGroup]);
  const fieldId = formGroup?.id || id;

  const fieldItem = (
    <FieldItem>
      <div>
        <SimpleCheckbox
          label={optionLabel}
          id={fieldId}
          showError={showError}
          data-test-error={showError}
          aria-labelledby={field.name}
          {...field}
          {...inputProps}
          // if value is null/undefined, default to empty string
          value={Boolean(field.value).toString()}
          // if value is null/undefined, explicitely cast to boolean
          // (this avoids switching from uncontrolled to controlled input)
          checked={Boolean(field.value)}
          onChange={() => {
            if (field.value === true) {
              setFieldValue(field.name, false);
            } else {
              setFieldValue(field.name, true);
            }
          }}
          disabledMessage={disabledMessage}
        />
      </div>

      {hint && (
        <FormHint
          isDisabled={props.disabled}
          data-testid={`${field.name}-hint`}
        >
          {hint}
        </FormHint>
      )}

      {showError && error && (
        <ErrorMessage
          data-testid={`${name}Error`}
          error={error}
          label={label}
        />
      )}
    </FieldItem>
  );

  return ((formGroup && !minTablet) || !formGroup) && !hideLabel ? (
    <FieldLayout stacked={stacked}>
      <FieldLabel
        name={name}
        displayLabel={label}
        fieldId={fieldId}
        info={info}
        showError={showError}
      />

      {fieldItem}
    </FieldLayout>
  ) : (
    fieldItem
  );
};
